import { useState, MouseEvent, useRef } from "react";
import { motion } from "framer-motion";
import { Volume2 } from "lucide-react";

interface FlashCardProps {
  letter: string;
  word: string;
  meaning: string;
  image?: string;
  letterAudio?: string;
  wordAudio?: string;
  color: string;
}

function FlashCard({
  letter,
  word,
  meaning,
  image,
  letterAudio,
  wordAudio,
  color,
}: FlashCardProps) {
  const [isFlipped, setIsFlipped] = useState(false);

  /* 🔊 Flip sound (loaded once) */
  const flipAudioRef = useRef<HTMLAudioElement | null>(null);
  if (!flipAudioRef.current) {
    flipAudioRef.current = new Audio("/sounds/card-flip.mp3");
    flipAudioRef.current.volume = 0.6; // soft, kid-friendly
  }

  /* ------------------ HANDLERS ------------------ */
  const handleFlip = () => {
    // play flip sound
    flipAudioRef.current?.currentTime && (flipAudioRef.current.currentTime = 0);
    flipAudioRef.current?.play().catch(() => {});

    setIsFlipped((prev) => !prev);
  };

  const playAudio = (src?: string, e?: MouseEvent) => {
    e?.stopPropagation(); // 🚫 prevent flip
    if (!src) return;

    const audio = new Audio(src);
    audio.play().catch((err) => console.error("Audio play error:", err));
  };

  /* Highlight main letter inside word */
  const renderHighlightedWord = (word: string, letter: string) => {
    const index = word.indexOf(letter);
    if (index === -1) return <span>{word}</span>;

    return (
      <span>
        {word.slice(0, index)}
        <span className="text-red-500 font-extrabold">{letter}</span>
        {word.slice(index + letter.length)}
      </span>
    );
  };

  /* ------------------ UI ------------------ */
  return (
    <div
      className="perspective-container h-56 cursor-pointer"
      onClick={handleFlip}
    >
      <motion.div
        className="relative w-full h-full"
        initial={false}
        animate={{ rotateY: isFlipped ? 180 : 0 }}
        transition={{ duration: 0.6, type: "spring", stiffness: 100 }}
        style={{ transformStyle: "preserve-3d" }}
      >
        {/* FRONT */}
        <div
          className={`absolute w-full h-full rounded-2xl shadow-lg flex flex-col items-center justify-center ${color}`}
          style={{ backfaceVisibility: "hidden" }}
        >
          <span className="text-7xl font-bold text-gray-800 mb-3">
            {letter}
          </span>

          {letterAudio && (
            <button
              onClick={(e) => playAudio(letterAudio, e)}
              className="px-3 py-1 rounded-full bg-indigo-100 text-indigo-700 text-xs font-semibold hover:bg-indigo-200 flex items-center gap-1"
            >
              <Volume2 className="w-3 h-3" />
              எழுத்தை கேள்
            </button>
          )}
        </div>

        {/* BACK */}
        <div
          className="absolute w-full h-full rounded-2xl shadow-lg flex flex-col items-center justify-center bg-white px-3"
          style={{
            backfaceVisibility: "hidden",
            transform: "rotateY(180deg)",
          }}
        >
          <span className="text-3xl font-bold text-gray-800 mb-2 text-center">
            {renderHighlightedWord(word, letter)}
          </span>

          {image && (
            <img
              src={image}
              alt={word}
              className="w-24 h-24 object-contain mb-2"
            />
          )}

          {wordAudio && (
            <button
              onClick={(e) => playAudio(wordAudio, e)}
              className="mb-2 px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 text-xs font-semibold flex items-center gap-1 hover:bg-emerald-200"
            >
              <Volume2 className="w-3 h-3" />
              சொல்லைக் கேள்
            </button>
          )}

          <span className="text-base text-gray-600 capitalize text-center">
            {meaning}
          </span>
        </div>
      </motion.div>
    </div>
  );
}

export default FlashCard;
